import {
  Deploy,
  FTEntryPointType,
  TransactorHashType,
} from '@block-explorer/entities';
import { MessageData } from './types';
import { guardedDeriveSplitDataFromArguments } from './utils';
import { NamedKeyPrefix } from '@make-software/cspr-design';

const getCep18TransactorsDataFromArgs = (args) => {
  const recipientHash = guardedDeriveSplitDataFromArguments(
    args.recipient,
    'Hash'
  );
  const recipientAccountHash = guardedDeriveSplitDataFromArguments(
    args.recipient,
    'Account'
  );
  const ownerHash = guardedDeriveSplitDataFromArguments(args.owner, 'Account');
  const toAccountHash = guardedDeriveSplitDataFromArguments(args.to, 'Account');
  const spenderHash = guardedDeriveSplitDataFromArguments(args.spender, 'Hash');

  return {
    recipientHash,
    recipientAccountHash,
    ownerHash,
    spenderHash,
    toAccountHash,
  };
};

const prepareActionMessageData = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any,
  t
): MessageData | null => {
  const { entryPoint, args } = deploy;
  const entryPointName = entryPoint?.name || '';

  const actionName: Record<FTEntryPointType, string> = {
    [FTEntryPointType.approve]: t('Approve transfer'),
    [FTEntryPointType.mint]: t('Mint'),
    [FTEntryPointType.burn]: t('Burn'),
    [FTEntryPointType.transfer]: t('Transfer'),
    [FTEntryPointType.transfer_with_authorization]: t('Authorized transfer'),
  };

  const getTokenSymbol = () => {
    if (
      entryPointName === 'mint' ||
      entryPointName === 'burn' ||
      entryPointName === 'transfer'
    ) {
      return 'tokens'; // contractPackage.metadata?.symbol;
    }

    return undefined;
  };

  const tokenSymbol = getTokenSymbol();

  if (entryPointName === FTEntryPointType.burn) {
    return {
      entryPointName: entryPointName,
      actionName: actionName[entryPointName] || entryPointName,
      ...(tokenSymbol && { symbol: tokenSymbol }),
    };
  }

  const {
    recipientHash,
    recipientAccountHash,
    ownerHash,
    spenderHash,
    toAccountHash,
  } = getCep18TransactorsDataFromArgs(args);

  const hasTransactorHashInArgs =
    recipientHash ||
    recipientAccountHash ||
    ownerHash ||
    spenderHash ||
    toAccountHash;

  if (actionName[entryPointName] === undefined || !hasTransactorHashInArgs) {
    return null;
  }

  const actionOutcome: Partial<MessageData> = {
    ...(tokenSymbol && { symbol: tokenSymbol }),
    // here could be a case when recipientAccountHash and recipientHash are both present
    // so, we need to avoid overwriting recipientAccountHash with recipientHash
    ...(recipientAccountHash?.hash
      ? {
          prefix2: t('to'),
          account2: {
            accountHash: recipientAccountHash?.hash,
            publicKey:
              recipientAccountHash?.hash &&
              getPublicKeyByAccountHash(recipientAccountHash.hash),
          },
        }
      : {
          prefix2: t('to'),
          account2: {
            hash: recipientHash?.hash,
          },
        }),
    ...(toAccountHash?.hash && {
      prefix2: t('to'),
      account2: {
        accountHash: toAccountHash?.hash,
        publicKey:
          toAccountHash?.hash && getPublicKeyByAccountHash(toAccountHash.hash),
      },
    }),
    ...(ownerHash?.hash && {
      prefix1: t('from'),
      account1: {
        accountHash: ownerHash?.hash,
        publicKey: ownerHash?.hash && getPublicKeyByAccountHash(ownerHash.hash),
      },
    }),
    ...(spenderHash?.hash && {
      prefix1: t('from'),
      account1: {
        hash: spenderHash?.hash,
      },
    }),
  };

  return {
    entryPointName: entryPointName,
    actionName: actionName[entryPointName] || entryPointName,
    ...actionOutcome,
  };
};

const prepareActionMessageDataForDeployDetails = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any,
  t
): MessageData | null => {
  const { entryPoint, args, contractPackage } = deploy;
  const entryPointName = entryPoint?.name || '';
  const amount =
    (deploy.args.amount?.parsed as string) ||
    (deploy.args.value?.parsed as string);

  const actionName = {
    [FTEntryPointType.transfer]: t('Transfer'),
    [FTEntryPointType.transfer_with_authorization]: t('Authorized transfer'),
    [FTEntryPointType.approve]: t('Approve transfer rights'),
    [FTEntryPointType.burn]: t('Burn'),
    [FTEntryPointType.mint]: t('Mint'),
  };

  const contractPrefixesMap = {
    [FTEntryPointType.transfer]: t('of'),
    [FTEntryPointType.transfer_with_authorization]: t('of'),
    [FTEntryPointType.burn]: t('of'),
    [FTEntryPointType.mint]: t('of'),
    [FTEntryPointType.approve]: t('for'),
  };

  const senderPrefixesMap = {
    [FTEntryPointType.transfer]: t('from'),
    [FTEntryPointType.transfer_with_authorization]: t('from'),
    [FTEntryPointType.burn]: t('owned by'),
    [FTEntryPointType.mint]: t('to'),
    [FTEntryPointType.approve]: t('to'),
  };

  const {
    recipientHash,
    recipientAccountHash,
    ownerHash,
    spenderHash,
    toAccountHash,
  } = getCep18TransactorsDataFromArgs(args);

  const hasTransactorHashInArgs =
    recipientHash ||
    recipientAccountHash ||
    ownerHash ||
    spenderHash ||
    toAccountHash;

  if (
    actionName[entryPointName] === undefined ||
    !amount ||
    !hasTransactorHashInArgs
  ) {
    return null;
  }

  const tokenSymbol = contractPackage.metadata?.symbol;
  const senderPrefix = senderPrefixesMap[entryPointName];
  const recipientPrefix = t('to');
  const contractPrefix = contractPrefixesMap[entryPointName];

  const isTransactorAccount = (transactor) =>
    transactor?.prefix === NamedKeyPrefix.ACCOUNT_HASH;

  const actionOutcome: Partial<MessageData> = {
    amount,
    ...(tokenSymbol && { symbol: tokenSymbol }),
    ...(contractPrefix && { contractPrefix }),
    ...(ownerHash?.hash && {
      prefix1: senderPrefix,
      account1: {
        hash: ownerHash?.hash,
        publicKey: ownerHash?.hash && getPublicKeyByAccountHash(ownerHash.hash),
        hashType: TransactorHashType.account,
      },
    }),
    ...(spenderHash?.hash && {
      prefix1: senderPrefix,
      account1: {
        hash: spenderHash?.hash,
        publicKey: isTransactorAccount(spenderHash)
          ? spenderHash?.hash && getPublicKeyByAccountHash(spenderHash.hash)
          : null,
        hashType: isTransactorAccount(spenderHash)
          ? TransactorHashType.account
          : TransactorHashType.hash,
      },
    }),
    // here could be a case when recipientAccountHash and recipientHash are both present
    // so, we need to avoid overwriting recipientAccountHash with recipientHash
    ...(recipientAccountHash?.hash
      ? {
          prefix2: recipientPrefix,
          account2: {
            hash: recipientAccountHash?.hash,
            publicKey:
              recipientAccountHash?.hash &&
              getPublicKeyByAccountHash(recipientAccountHash.hash),
            hashType: TransactorHashType.account,
          },
        }
      : {
          prefix2: recipientPrefix,
          account2: {
            hash: recipientHash?.hash,
            hashType: TransactorHashType.hash,
          },
        }),
    ...(toAccountHash?.hash && {
      prefix2: recipientPrefix,
      account2: {
        hash: toAccountHash?.hash,
        publicKey:
          toAccountHash?.hash && getPublicKeyByAccountHash(toAccountHash.hash),
        hashType: TransactorHashType.account,
      },
    }),
  };

  return {
    entryPointName: entryPointName,
    actionName: actionName[entryPointName] || entryPointName,
    ...actionOutcome,
  };
};

export const ftTokenMessage = () => {
  const getRelevantAccountHashesFromArgs = () => {
    return ['owner', 'spender', 'recipient'];
  };

  return {
    prepareActionMessageData,
    prepareActionMessageDataForDeployDetails,
    getRelevantAccountHashesFromArgs,
  };
};
